import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import NoticeMessage from '@/plugin/noticemessage/noticemessage.js';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import { APIS, URLS } from '@/shared/config/urls';
import i18n, { fetchTranslation } from '@/shared/utils/i18n';
import { getFirstAccessibleRoute } from '@/shared/utils/navigation';
import { request } from '@/shared/utils/request';
import { useAuthStore } from '@/store/authStore';
import { useLoadingStore } from '@/store/loadingStore';
import type {
  CheckStatusResponse,
  ForgotPasswordRequest,
  SignInRequest,
  SignInResponse,
} from '../types/auth.types';

const DEFAULT_LANDING_ROUTE = '/dashboard/smartCity-dashboard';

const useAuth = ({
  userID = null,
  onLoginFail = (_err?: unknown) => {},
  onResetFail = (_err?: unknown) => {},
  // onSuccessUpdate = () => {},
}: {
  userID?: string | null;
  onLoginFail?: (err?: unknown) => void;
  onResetFail?: (err?: unknown) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetches the same navbar/authority list Sidebar.tsx and Header.tsx use to
  // hide inaccessible items, so a user lands on whatever page they can
  // actually use instead of always the Smart City dashboard - falls back to
  // it only when the list can't be resolved (fetch failure or no grants).
  const resolveLandingRoute = async (): Promise<string> => {
    try {
      const requestURL = `${URLS.BACK_DSH}${APIS.accessMenu}`;
      const navbarList = await request.get<NavbarItem[]>(requestURL);
      return getFirstAccessibleRoute(navbarList) ?? DEFAULT_LANDING_ROUTE;
    } catch {
      return DEFAULT_LANDING_ROUTE;
    }
  };

  const handleLogin = async (accountDT: SignInRequest) => {
    const requestURL = `${URLS.BACK_DSH}${APIS.login}`;
    return request
      .post<SignInResponse>(requestURL, accountDT)
      .then(async (data) => {
        // The web app always starts in English after login; the ID/EN toggle
        // only changes the language for the current session. The backend
        // serves the dictionary and menu labels from the stored preference
        // (login.py returns it as `lang`), so when the stored value is not
        // English it is reset first - otherwise the text would come back in
        // the language chosen last time while the toggle shows EN.
        const lang = 'eng';
        const storedLang = data.lang ?? data.language;
        useAuthStore.getState().setAuth({
          user_id: data.user_id,
          group_id: data.group_id,
          lang,
          rememberMe: accountDT?.remember_me === true,
        });
        // Internal vs hospital account (and which facility), as the backend
        // resolved it. Screens read it from the store; the units enforce it.
        useAuthStore.getState().setAccountScope(data);
        // Loader global selama kamus bahasa dimuat setelah login.
        const { setTranslationLoading } = useLoadingStore.getState();
        setTranslationLoading(true);
        try {
          if (storedLang !== lang) {
            try {
              await request.put(`${URLS.BACK_DSH}${APIS.updateLanguagePreference}`, {
                language_preference: lang,
              });
            } catch (err) {
              console.error('Error resetting language preference:', err);
            }
          }
          await i18n.changeLanguage(lang);
          await fetchTranslation(lang);
        } finally {
          setTranslationLoading(false);
        }
        queryClient.clear();
        navigate(await resolveLandingRoute());
      })
      .catch((err) => {
        onLoginFail(err);
      });
  };

  const handleLogout = async () => {
    const requestURL = `${URLS.BACK_DSH}${APIS.logout}`;
    return request.get(requestURL).then(() => {
      useAuthStore.getState().sendLogoutSignal();
      useAuthStore.getState().clearAuth();
      queryClient.clear();

      navigate('/');
    });
  };

  const { data: handleAuthority } = useQuery({
    queryKey: ['navbarList'],
    queryFn: async () => {
      // const requestURL = `${URLS.BACK_DSH}${APIS.accessMenu}`;
      // const response = await request.get(requestURL);
      // return response;
      const requestURL = `${URLS.BACK_DSH}${APIS.accessMenu}`;
      return request.get<NavbarItem[]>(requestURL);
    },
    enabled: !!userID,
    staleTime: 1000 * 60 * 1,
  });
  // console.log(handleAuthority);

  const getIsLogin = () => {
    return useAuthStore.getState().isLogin;
  };

  const handleForgotPassword = async (accountDT: ForgotPasswordRequest) => {
    const requestURL = `${URLS.BACK_DSH}${APIS.forgotPassword}`;
    return request
      .post(requestURL, accountDT)
      .then((_data) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(`성공적으로 업데이트하였습니다.`, {
          callback() {
            location.reload();
          },
        });
      })
      .catch((err) => {
        onResetFail(err);
      });
  };

  const checkStatus = async () => {
    request
      .get<CheckStatusResponse>(`${URLS.BACK_DSH}${APIS.checkStatus}`)
      .then((response) => {
        if (response.ok === false) {
          useAuthStore.getState().clearAuth();
          queryClient.clear();
          navigate('/');
          return;
        }
        // The probe also carries the account scope, so a session restored from
        // storage (or one whose hospital link changed) picks up the current
        // value instead of the one persisted at login.
        useAuthStore.getState().setAccountScope(response);
      })
      .catch(() => {
        // 401/403 is handled by the axios response interceptor in request.ts
        useAuthStore.getState().clearAuth();
        queryClient.clear();
        navigate('/');
      });
  };

  return {
    checkStatus,
    getIsLogin,
    handleLogin,
    handleLogout,
    resolveLandingRoute,
    handleForgotPassword,
    handleAuthority,
  };
};

export default useAuth;
